import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, ChevronLeft, Mail, Phone, MapPin, Calendar, Camera, Upload } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';

const EditProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    pincode: user?.pincode || '',
    dob: user?.dob ? new Date(user.dob).toISOString().split('T')[0] : ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setAvatarLoading(true);

    try {
      const base64 = await convertToBase64(file);
      await userService.updateAvatar(base64);
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error('Upload failed');
      setAvatarPreview(user?.avatar || '');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await userService.updateProfile(formData);
      toast.success('Profile updated!');
      navigate('/store');
    } catch (error) {
      toast.error('Profile update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-20">

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/store" className="p-2 hover:bg-white/20 rounded-lg">
            <ChevronLeft size={24} />
          </Link>
          <User size={28} />
          <h1 className="text-xl font-bold">Edit Profile</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4">
        <div className="bg-white rounded-xl shadow-sm p-6">

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Avatar */}
            <div className="flex flex-col items-center mb-6">

              <div className="relative">

                <div className="w-32 h-32 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">

                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : formData.name ? (
                    <span className="text-5xl font-bold text-white">
                      {formData.name.charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <User size={64} className="text-white" />
                  )}

                </div>

                <div className="absolute bottom-1 right-1 bg-purple-600 text-white p-2 rounded-full shadow-lg">
                  {avatarLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera size={20} />
                  )}
                </div>

              </div>

              {/* Visible File Upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="mt-4"
              />

              <p className="text-sm text-gray-500 mt-2">
                Upload profile photo (max 2MB)
              </p>

            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>

              <div className="relative">
                <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border rounded-xl"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>

              <div className="relative">
                <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border rounded-xl"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>

              <div className="relative">
                <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border rounded-xl"
                />
              </div>
            </div>

            {/* DOB */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>

              <div className="relative">
                <Calendar size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border rounded-xl"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>

              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
                className="w-full border rounded-xl p-3"
              />
            </div>

            {/* Pincode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode
              </label>

              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                className="w-full border rounded-xl p-3"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">

              <Link
                to="/store"
                className="flex-1 py-3 text-center border rounded-xl"
              >
                Cancel
              </Link>

              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 bg-purple-600 text-white rounded-xl"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>

            </div>

          </form>

        </div>
      </div>
    </div>
  );
};

export default EditProfile;